import { useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { startOfMinute } from 'date-fns';
import { styled } from '@mui/material/styles';
import Button from '@components/Button';
import { Chip } from '@components/Chip';
import { Typography } from '@components/Typography';
import { MessageType } from '@constants/inbox';
import { handleApiError } from '@helpers/api';
import { useTranslation } from '@hooks';
import { agentsService } from '@services';
import { ResetIcon } from 'assets/images/icons';
import { toAgentValues } from '../agentContactFields';
import PulseAIInput from './PulseAIInput';
import Message from './Message';
import TypingIndicator from './TypeIndicator';

const Container = styled('div')({
    height: 'calc(var(--100vh) - 154px)',
    display: 'grid',
    gridGap: '16px',
    gridTemplateRows: '32px auto max-content',
    gridTemplateColumns: '100%',
    justifyContent: 'center',
    backgroundColor: '#f2f2f2',
    padding: '24px 16px',
    '@media (max-width: 768px)': {
        height: 'calc(var(--100vh) - 122px)',
        gridGap: '8px',
    },
});

export const Feed = styled('div')({
    overflowY: 'auto',
    display: 'flex',
    flexFlow: 'column-reverse',
    gap: 8,
    '@media (max-width: 768px)': {
        padding: 8,
    },
});

const SuggestedReplies = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: 8,
});

const PulseAIPlayground = ({ selectedAgentRef }) => {
    const [data, setData] = useState([]);
    const [isTyping, setIsTyping] = useState(false);

    const currentUser = useSelector(state => state.auth.user);

    const { t } = useTranslation();
    const { getValues } = useFormContext();
    const conversationIdRef = useRef(uuidv4());

    const sendMessage = async text => {
        if (!text || isTyping) return;
        setIsTyping(true);
        const { days, hours, minutes, ...values } = getValues();
        setData(prev => [
            ...prev,
            { id: uuidv4(), message: text, createdAt: new Date().toISOString(), type: MessageType.Outgoing },
        ]);
        try {
            const response = await agentsService.sendPlaygroundMessage({
                conversationId: conversationIdRef.current,
                message: text,
                agent: {
                    ...selectedAgentRef.current,
                    ...toAgentValues(values),
                    id: selectedAgentRef.current?.id || null,
                    status: Number(values.status),
                    handoff: +values.handoff,
                    autoCloseDelay: `${days}:${hours}:${minutes}`,
                },
            });
            const replies = response.messages.map((messageText, index, all) => ({
                id: uuidv4(),
                message: messageText,
                createdAt: new Date().toISOString(),
                type: MessageType.Incoming,
                suggestedReplies: index === all.length - 1 ? response.suggestedReplies : undefined,
            }));
            setData(prev => [...prev, ...replies]);
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsTyping(false);
        }
    };

    const lastMessage = data[data.length - 1];
    const suggestedReplies =
        !isTyping && lastMessage?.type === MessageType.Incoming ? (lastMessage.suggestedReplies ?? []) : [];

    const messages = useMemo(() => {
        if (!data.length) return [];
        const sortedMessages = [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const getNormalizedDate = dateStr => startOfMinute(new Date(dateStr)).getTime();

        const createMessageObject = msg => ({ id: msg.id, message: msg.message, createdAt: msg.createdAt });

        return sortedMessages.reduce((groups, message) => {
            const lastGroup = groups[groups.length - 1];
            const messageTime = getNormalizedDate(message.createdAt);

            const shouldCreateNewGroup =
                !lastGroup ||
                messageTime !== getNormalizedDate(lastGroup.createdAt) ||
                message.type !== lastGroup.type ||
                message.modelType !== lastGroup.modelType;

            if (shouldCreateNewGroup) {
                groups.push({
                    ...message,
                    children: [createMessageObject(message)],
                    createdAt: new Date(messageTime),
                });
            } else {
                lastGroup.children.push(createMessageObject(message));
                lastGroup.children.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            }

            return groups;
        }, []);
    }, [data]);

    const onReset = () => {
        setData([]);
        conversationIdRef.current = uuidv4();
    };

    const agent = selectedAgentRef.current;
    const hasKnowledge = !!agent?.knowledgeBaseCount;

    if (!hasKnowledge) {
        return (
            <Container sx={{ gridTemplateRows: '100%', alignItems: 'center' }}>
                <Typography
                    variant="body2"
                    align="center"
                    sx={{ color: 'var(--text-secondary)', maxWidth: 400, margin: '0 auto' }}
                >
                    {agent ? t('Agents.playgroundAssignContent') : t('Agents.playgroundSaveAgentFirst')}
                </Typography>
            </Container>
        );
    }

    return (
        <Container>
            <Button
                variant="outlined"
                sx={{ backgroundColor: '#fff', margin: '0 auto' }}
                startIcon={<ResetIcon />}
                onClick={onReset}
            >
                {t('buttons.reset')}
            </Button>
            <Feed>
                {isTyping && <TypingIndicator />}
                {suggestedReplies.length > 0 && (
                    <SuggestedReplies>
                        {suggestedReplies.map(label => (
                            <Chip key={label} label={label} onClick={() => sendMessage(label)} />
                        ))}
                    </SuggestedReplies>
                )}
                {messages.map((item, index) => (
                    <Message key={index} data={item} currentUser={currentUser} />
                ))}
            </Feed>
            <PulseAIInput onSend={sendMessage} isTyping={isTyping} />
        </Container>
    );
};

export default PulseAIPlayground;
